import { Button, Col, Form, Input, InputNumber, Modal, Radio, Row, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from '../../page.module.scss';
import { BagMap } from '@/lib/components/bag/bagMap';
import { useEffect, useState } from 'react';
import { useFeature } from '@/lib/hooks/useFeature';
import { Relatie, OwnershipType, getRegistrationYear, hasOwnershipStatus } from '@/lib/types';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';

export const Bedrijfsgegevens: React.FunctionComponent = () => {
  const { t } = useTranslation('input');
  const form = useFormInstance<Relatie>();
  const [modalIsShown, setModalIsShown] = useState(false);
  const isBagEnabled = useFeature('show-bag-koppeling');
  const registratiejaar = Form.useWatch('registratiejaar', { form, preserve: true });
  const showOwnershipStatus = hasOwnershipStatus(registratiejaar);
  const isOwnershipStatusUnsupportedYear = Number.isFinite(getRegistrationYear(registratiejaar)) && !showOwnershipStatus;
  const requiresTotaleBeteelbareKasOppervlakte = Number(registratiejaar) >= 2026;

  useEffect(() => {
    if (isOwnershipStatusUnsupportedYear) {
      form.setFieldsValue({
        eigendomstypeDeelnemer: undefined,
      });
    }
  }, [form, isOwnershipStatusUnsupportedYear]);

  useEffect(() => {
    if (!requiresTotaleBeteelbareKasOppervlakte) {
      form.setFields([{ name: 'totaleBeteelbareKasOppervlakte', errors: [] }]);
    }
  }, [form, requiresTotaleBeteelbareKasOppervlakte]);

  return (
    <>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.uonumber')}
        </Col>
        <Col span={3}>
          <Form.Item name={'uoNummer'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.postalAddress')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'postAdresStraat'}>
            <Input />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={1}>
          {t('form.companyInfo.houseNumber')}
        </Col>
        <Col span={1}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'postAdresHuisnummer'}>
            <Input />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={2}>
          {t('form.companyInfo.addition')}
        </Col>
        <Col span={1}>
          <Form.Item name={'postAdresHuisnummerToevoeging'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.kvkNumber')}
        </Col>
        <Col span={3}>
          <Form.Item
            required
            hasFeedback
            name="kvkNummer"
            rules={[
              {
                required: true,
                message: 'Het KVK-nummer is verplicht.',
              },
              {
                pattern: /^\d{8}$/,
                message: 'Het KVK-nummer moet precies 8 cijfers bevatten.',
              },
            ]}>
            <Input maxLength={8} />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.zipcode')}
        </Col>
        <Col span={3}>
          <Form.Item
            name="postAdresPostCodeCijfers"
            rules={[
              { required: true, message: '' },
              { pattern: /^\d{4}$/, message: '' },
            ]}>
            <Input
              maxLength={4}
              onChange={e => {
                const onlyDigits = e.target.value.replace(/\D/g, '');
                e.target.value = onlyDigits;
              }}
            />
          </Form.Item>
        </Col>

        <Col span={1}>
          <Form.Item
            name="postAdresPostCodeLetters"
            rules={[
              { required: true, message: '' },
              { pattern: /^[a-zA-Z]{2}$/, message: '' }, // allow both upper and lower case
            ]}>
            <Input
              maxLength={2}
              onChange={e => {
                const onlyLetters = e.target.value.replace(/[^a-zA-Z]/g, '');
                e.target.value = onlyLetters;
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col span={6} />
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.city')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'postAdresPlaats'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.companyName')}
        </Col>
        <Col span={3}>
          <Form.Item name={'bedrijfsnaam'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.phoneNumber')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'telefoon'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.contactPerson')}
        </Col>
        <Col span={3}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'contactpersoon'}>
            <Input />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.url')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: false, message: '' }]} name={'internet'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col span={6} />
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.fax')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'fax'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.vestigingsAdresStraat')}
        </Col>
        <Col span={3}>
          <Form.Item name={'vestigingsAdresStraat'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.email')}
        </Col>
        <Col span={4}>
          <Form.Item rules={[{ required: true, message: '' }]} name={'email'}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.vestigingsAdresPostCode')}
        </Col>
        <Col span={2}>
          <Form.Item name={'vestigingsAdresPostCodeCijfers'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={1}>
          <Form.Item name={'vestigingsAdresPostCodeLetters'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.localAuthority')}
        </Col>
        <Col span={4}>
          <Form.Item name={'gemeente'}>
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>
      <Row className={styles.rowWithoutBorder}>
        <Col className={styles.blueTextLeft} span={3}>
          {t('form.companyInfo.plaats')}
        </Col>
        <Col span={3}>
          <Form.Item name={'vestigingsAdresPlaats'}>
            <Input disabled />
          </Form.Item>
        </Col>
        <Col className={styles.blueTextRight} span={3}>
          {t('form.companyInfo.localAuthorityWater')}
        </Col>
        <Col span={4}>
          <Form.Item name={'waterschap'}>
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>

      {requiresTotaleBeteelbareKasOppervlakte && (
        <Row className={styles.rowWithoutBorder} style={{ marginBottom: '38px' }}>
          <Col className={styles.blueTextLeft} span={3}>
            {t('form.companyInfo.totaleBeteelbareKasOppervlakte')}
          </Col>
          <Col span={3}>
            <Form.Item
              rules={[
                { required: true, message: '' },
                { type: 'number', min: 1, message: 'Waarde mag niet 0 zijn' },
              ]}
              name={'totaleBeteelbareKasOppervlakte'}>
              <InputNumber step={1} min={0} max={999999999} addonAfter={'m²'} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={1} style={{ display: 'flex', alignItems: 'center', height: '100%', paddingLeft: '12px' }}>
            <Tooltip
              title={
                <>
                  <p>{t('form.companyInfo.totaleBeteelbareKasOppervlakteTooltip')}</p>
                  <p><strong>{t('form.companyInfo.beteelbaarOppervlakKenmerken')}</strong></p>
                  <ul>
                    <li>{t('form.companyInfo.beteelbaarOppervlakDefinitie')}</li>
                    <li>{t('form.companyInfo.beteelbaarOppervlakRegistratiejaar')}</li>
                    <li>{t('form.companyInfo.beteelbaarOppervlakVerhouding')}</li>
                    <li>
                      {t('form.companyInfo.beteelbaarOppervlakVoorbeelden')}
                      <ul>
                        <li>{t('form.companyInfo.beteelbaarOppervlakEnkeleTeeltlaag')}</li>
                        <li>{t('form.companyInfo.beteelbaarOppervlakMeerdereTeeltlagen')}</li>
                      </ul>
                    </li>
                  </ul>
                </>
              }
              trigger={['hover', 'focus', 'click']}
              placement="right"
              classNames={{ root: styles.infoTooltip }}>
              <InfoCircleOutlined tabIndex={0} aria-label="Toelichting beteelbaar oppervlak" style={{ cursor: 'pointer', color: 'var(--color--primary)' }} />
            </Tooltip>
          </Col>
          <Col span={6} />
        </Row>
      )}

      {isBagEnabled && (
        <>
          <Row className={styles.rowWithoutBorder}>
            <Col className={styles.blueTextLeft} span={3}>
              {t('form.companyInfo.bagKoppeling')}
            </Col>
            <Col span={3}>
              <Form.Item name={'pandId'} rules={[{ required: true, message: 'Koppel je bedrijf aan een pand.' }]}>
                {form.getFieldValue('pandId') ? (
                  <Input value={form.getFieldValue('pandId')} style={{ width: '100%' }} readOnly />
                ) : (
                  <Button type="default" style={{ width: '100%' }} onClick={() => setModalIsShown(true)}>
                    Pand koppelen
                  </Button>
                )}
              </Form.Item>
            </Col>
            {form.getFieldValue('pandId') && (
              <Col>
                <Button shape="circle" icon={<EditOutlined />} style={{ marginLeft: '10px' }} onClick={() => setModalIsShown(true)} />
              </Col>
            )}
          </Row>

          <Modal title={'Bedrijf koppelen aan pand'} open={modalIsShown} onCancel={() => setModalIsShown(false)} width={'80%'} footer={null}>
            <BagMap modalIsShown={modalIsShown} setModalIsShown={setModalIsShown} />
          </Modal>
        </>
      )}
      {showOwnershipStatus && (
        <Row style={{ marginBottom: '16px' }}>
          <Col span={24}>
            <Form.Item label={t('form.companyInfo.ownership.question')} labelCol={{ span: 24 }} name="eigendomstypeDeelnemer" wrapperCol={{ span: 24 }}>
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Radio value={OwnershipType.OwnCompany}>{t('form.companyInfo.ownership.option_1')}</Radio>
                <Radio value={OwnershipType.GroupCompany}>{t('form.companyInfo.ownership.option_2')}</Radio>
                <Radio value={OwnershipType.ThirdPartyLease}>{t('form.companyInfo.ownership.option_3')}</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};
